import { memo, useCallback, useMemo } from 'react';
import { Button, SegmentedControl, Switch } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { ACCESS_LEVEL_META } from '../../../constants/copy';
import { permissionTextKey } from '../../../utils/catalogText';
import { ORIGIN, isBlockedByCeiling, isBroaderLevel, isLevelLocked, levelsOf } from '../../../utils/permissionTree';
import PartialBadge from '../PartialBadge';
import PermissionLevelSuffix from '../PermissionLevelSuffix';
import PermissionSourceTag from '../PermissionSourceTag';
import RoleSourceTags from '../RoleSourceTags';

const INDENT_PX = 16;
// Level control (14rem) + gap (0.75rem) + switch (2.75rem): the right column is as wide as the three
// together, so rows and switches line up. Written whole because Tailwind needs the full class names.
const LEVEL_CONTROL_WIDTH = 'w-56';
const CONTROLS_COLUMN_WIDTH = 'sm:w-[17.5rem]';

/**
 * One row of the `PermissionTree`: expand chevron (parents), label, Partial badge, source tags,
 * description, and on the right the access level (read-only suffix or Own / Groups / Tenant
 * control) plus the switch.
 *
 * @param {object} props
 * @param {{ id: string, label: string, description?: string, children?: object[] }} props.node
 * @param {{ on: boolean, level: string, origin: string, via: string[] }} props.entry Draft entry.
 * @param {string|null} [props.ceilingId] Id of the parent row while it is on (it limits this row), else `null`.
 * @param {string} [props.ceilingLabel] Catalog label of that parent.
 * @param {string|null} [props.ceilingLevel] Level of that parent. Levels broader than it are disabled.
 * @param {{ checked: boolean, partial: boolean, on: number, total: number }} props.state From `getNodeState`.
 * @param {{ partial: boolean, on: number, total: number }} [props.badgeState] `getNodeState` with `effective`, for the Partial badge (defaults to `state`).
 * @param {string[]} [props.viaRoles] Roles the row is inherited from (see `getViaRoles`).
 * @param {'pinned'|'modified'} [props.tag] Edit-time tag from `diffDraft`.
 * @param {'read'|'edit'} props.mode
 * @param {number} props.depth
 * @param {boolean} props.expanded
 * @param {(id: string, on: boolean) => void} props.onToggle
 * @param {(id: string, level: string) => void} props.onLevelChange
 * @param {(id: string) => void} props.onExpand
 */
const PermissionRow = ({ node, entry, ceilingId = null, ceilingLabel, ceilingLevel = null, state, badgeState, viaRoles = [], tag, mode, depth, expanded, onToggle, onLevelChange, onExpand }) => {
  const { t } = useTranslation('people-access');
  const isEdit = mode === 'edit';
  const label = t(permissionTextKey(node.id, 'label'), { defaultValue: node.label });
  const description = node.description
    ? t(permissionTextKey(node.id, 'description'), { defaultValue: node.description })
    : null;
  const hasChildren = Boolean(node.children?.length);
  const inherited = entry.origin === ORIGIN.ROLE;
  const locked = isEdit && isLevelLocked(entry);
  // The "Partial" badge counts rows a role grants too, in the edit view as well as in the read view.
  const badge = badgeState ?? state;
  // The row of a parent toggles it (like the prototype); the chevron and the controls on the right
  // (switch, level) stop the click so it is not handled twice or does not fold the row.
  const handleRowClick = useCallback(() => onExpand(node.id), [onExpand, node.id]);
  const handleChevron = useCallback(
    (event) => {
      event.stopPropagation();
      onExpand(node.id);
    },
    [onExpand, node.id],
  );
  const stopPropagation = useCallback((event) => event.stopPropagation(), []);
  const showLevelSuffix = !isEdit && (hasChildren ? state.checked || state.partial : state.checked);

  const hasCeiling = ceilingId != null && ceilingLevel != null;
  const ceilingLevelLabel = hasCeiling
    ? ACCESS_LEVEL_META[ceilingLevel]
      ? t(ACCESS_LEVEL_META[ceilingLevel].labelKey)
      : ceilingLevel
    : null;
  const tooBroadReason = hasCeiling
    ? t('permissionTree.levelTooBroad', {
        name: t(permissionTextKey(ceilingId, 'label'), { defaultValue: ceilingLabel }),
        level: ceilingLevelLabel,
      })
    : null;
  const offeredLevels = useMemo(() => levelsOf(node), [node]);
  const showTooBroad = isEdit && hasCeiling && offeredLevels.some((level) => isBroaderLevel(level, ceilingLevel));
  // A permission that cannot be on under a narrower parent: the switch stays off, the reason is shown.
  const blocked = isEdit && hasCeiling && !entry.on && isBlockedByCeiling(node, ceilingLevel);
  const reasonId = `permission-reason-${node.id}`;
  const levelOptions = offeredLevels.map((level) => {
    const tooBroad = hasCeiling && isBroaderLevel(level, ceilingLevel);
    const hint = t(`permissionTree.levelHint.${level}`);
    return {
      value: level,
      label: t(ACCESS_LEVEL_META[level].labelKey),
      disabled: locked || tooBroad,
      title: tooBroad ? `${hint} ${tooBroadReason}` : hint,
    };
  });

  return (
    <div
      data-testid={`permission-row-${node.id}`}
      className={['flex flex-wrap items-start gap-x-3 gap-y-2 border-b border-edge py-3 last:border-b-0', hasChildren ? 'cursor-pointer' : '']
        .filter(Boolean)
        .join(' ')}
      style={{ paddingLeft: depth * INDENT_PX }}
      onClick={hasChildren ? handleRowClick : undefined}
    >
      <div className="flex w-6 shrink-0 justify-center pt-0.5">
        {hasChildren ? (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={expanded ? ChevronDown : ChevronRight}
            aria-expanded={expanded}
            aria-label={t(expanded ? 'permissionTree.collapse' : 'permissionTree.expand', { name: label })}
            onClick={handleChevron}
          />
        ) : null}
      </div>

      <div className="min-w-0 flex-1 basis-40">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-content">{label}</span>
          {badge.partial ? <PartialBadge on={badge.on} total={badge.total} /> : null}
          {viaRoles.length ? <RoleSourceTags roles={viaRoles} /> : null}
          {tag ? <PermissionSourceTag source={tag} /> : null}
          {showLevelSuffix ? <PermissionLevelSuffix level={entry.level} /> : null}
        </div>
        {description ? <p className="text-xs text-content-muted">{description}</p> : null}
        {isEdit && inherited && !entry.on ? (
          <p className="text-xs italic text-content-muted">{t('permissionTree.inheritedNote')}</p>
        ) : null}
      </div>

      {/* Under the text on a narrow screen, on the right from `sm`. */}
      <div
        className={`flex w-full shrink-0 cursor-default flex-col items-end gap-1 ${isEdit ? CONTROLS_COLUMN_WIDTH : 'sm:w-auto'}`}
        onClick={stopPropagation}
      >
        <div className="flex items-center justify-end gap-3">
          {isEdit ? (
            <SegmentedControl
              ariaLabel={t('permissionTree.levelAria', { name: label })}
              value={entry.level}
              options={levelOptions}
              onChange={(level) => onLevelChange(node.id, level)}
              ariaDescribedBy={showTooBroad ? reasonId : undefined}
              className={`${LEVEL_CONTROL_WIDTH} max-w-full ${locked || !entry.on ? 'opacity-50' : ''}`}
            />
          ) : null}
          <Switch
            size="small"
            aria-label={label}
            checked={state.checked}
            disabled={!isEdit || blocked}
            onChange={(next) => onToggle(node.id, next)}
          />
        </div>
        {showTooBroad ? (
          <p id={reasonId} className="w-full text-right text-xs text-content-muted">
            {tooBroadReason}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default memo(PermissionRow);
